import React, { useState, useEffect, useMemo } from 'react';
import { Link, NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { isPlatformAdminUser } from '../lib/platformAdmin';
import {
  LogOut, Settings as SettingsIcon,
  FlaskConical, BookOpen, BarChart3, Wallet, Menu, X, FileText, Code2, Building2, UserRound,
  Bell, ChevronDown, ChevronLeft, ChevronRight, Users,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { DashboardBreadcrumbProvider } from './DashboardBreadcrumbContext';
import { LogoMark } from './Logo';

// Header is a fixed bar across the top. The desktop sidebar is fixed flush
// against the left edge of the viewport; the main content gets a left padding
// so it never sits under the sidebar. On mobile, the sidebar becomes a
// slide-in drawer triggered by a hamburger button in the header.
const linkBase   = 'flex items-center gap-2 px-3 py-2 text-sm border border-transparent rounded-sm transition-colors';
const linkActive = 'bg-zinc-900 text-white border-zinc-900';
const linkIdle   = 'text-zinc-700 hover:bg-zinc-100';
const SIDEBAR_COLLAPSED_KEY = 'idemgo.sidebarCollapsed';
const APP_CANVAS_BG = 'bg-white';

const NAV_SECTIONS = [
  {
    label: 'Verifications',
    items: [
      {
        to: '/dashboard/verifications',
        end: true,
        label: 'Individuals',
        icon: UserRound,
        testid: 'nav-verifications',
        activePrefixes: ['/dashboard/verifications', '/verifications'],
      },
      {
        to: '/dashboard/businesses',
        label: 'Businesses',
        icon: Building2,
        testid: 'nav-businesses',
        activePrefixes: ['/dashboard/businesses'],
      },
    ],
  },
  {
    label: 'Documents',
    items: [
      {
        to: '/dashboard/signatures',
        label: 'Documents',
        icon: FileText,
        testid: 'nav-signatures',
        activePrefixes: ['/dashboard/signatures', '/dashboard/documents', '/documents'],
      },
    ],
  },
  {
    label: 'Insights',
    items: [
      { to: '/usage', label: 'Usage', icon: BarChart3, testid: 'nav-usage' },
      { to: '/billing', label: 'Billing', icon: Wallet, testid: 'nav-billing' },
    ],
  },
  {
    label: 'Developers',
    items: [
      { to: '/dashboard/integration', label: 'Integration', icon: Code2, testid: 'nav-integration' },
      { to: '/dashboard/docs', label: 'Docs', icon: BookOpen, testid: 'nav-docs' },
    ],
  },
  {
    label: 'Settings',
    items: [
      { to: '/settings', label: 'Organization', icon: SettingsIcon, testid: 'nav-settings' },
    ],
  },
];

function NavLinks({ onNavigate, collapsed = false }) {
  const location = useLocation();
  const pathname = location.pathname;
  const navSections = NAV_SECTIONS;

  return (
    <>
      {navSections.map((section, sectionIndex) => (
        <div key={section.label} className={`flex flex-col gap-1 ${collapsed ? 'items-center' : ''}`}>
          {section.label && !collapsed && (
            <div className={`px-3 ${sectionIndex === 0 ? 'pt-0' : 'pt-3'} pb-1 text-[10px] uppercase tracking-widest font-mono text-zinc-400`}>
              {section.label}
            </div>
          )}
          {section.items.map(({ to, end, label, icon: Icon, testid, activePrefixes }) => {
            const isActive = activePrefixes
              ? pathname === to || activePrefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
              : end
                ? pathname === to
                : pathname === to || pathname.startsWith(`${to}/`);

            return (
              <NavLink key={to} to={to} end={end} data-testid={testid} onClick={onNavigate}
                title={collapsed ? label : undefined}
                aria-label={collapsed ? label : undefined}
                className={`${linkBase} ${collapsed ? 'h-9 w-9 justify-center gap-0 px-0 py-0' : ''} ${isActive ? linkActive : linkIdle}`}>
                <Icon className="w-4 h-4 shrink-0" strokeWidth={1.5}/>
                {!collapsed && <span>{label}</span>}
              </NavLink>
            );
          })}
        </div>
      ))}
    </>
  );
}

function AccountMenu({ user, onLogout, onNavigate, side = 'bottom', align = 'end', className = '' }) {
  const displayName = user?.full_name || user?.name || 'Account';
  const workspaceName = user?.organization_name || 'Workspace';
  const workspaceInitial = workspaceName.trim().charAt(0).toUpperCase() || 'W';
  const role = user?.role || '';

  const go = (path) => {
    onNavigate(path);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button"
          className={`inline-flex min-w-0 items-center justify-between gap-2 border border-zinc-200 rounded-sm bg-white px-2 py-1.5 text-sm text-zinc-700 hover:border-zinc-400 transition-colors ${className}`}>
          <span className="grid h-6 w-6 shrink-0 place-items-center rounded-sm bg-black font-mono text-[10px] font-semibold uppercase tracking-widest text-white">
            {workspaceInitial}
          </span>
          <span className="truncate">{workspaceName}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-zinc-400" strokeWidth={1.5}/>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side={side} align={align} className="w-64 rounded-sm border-zinc-200 bg-white p-1 shadow-sm">
        <div className="px-3 py-2">
          <div className="truncate text-sm font-medium text-zinc-900">{displayName}</div>
          <div className="mt-1 truncate font-mono text-xs text-zinc-500">{user?.email || '—'}</div>
          {role && (
            <span className="mt-2 inline-flex items-center border border-zinc-200 bg-white px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
              {role}
            </span>
          )}
        </div>
        <DropdownMenuSeparator className="bg-zinc-100"/>
        <DropdownMenuItem onSelect={() => go('/settings')} className="cursor-pointer text-zinc-700 focus:bg-zinc-50 focus:text-zinc-900">
          <SettingsIcon className="h-4 w-4" strokeWidth={1.5}/>
          Organization settings
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => go('/settings')} className="cursor-pointer text-zinc-700 focus:bg-zinc-50 focus:text-zinc-900">
          <Users className="h-4 w-4" strokeWidth={1.5}/>
          Members
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => go('/usage')} className="cursor-pointer text-zinc-700 focus:bg-zinc-50 focus:text-zinc-900">
          <BarChart3 className="h-4 w-4" strokeWidth={1.5}/>
          Usage
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => go('/billing')} className="cursor-pointer text-zinc-700 focus:bg-zinc-50 focus:text-zinc-900">
          <Wallet className="h-4 w-4" strokeWidth={1.5}/>
          Billing
        </DropdownMenuItem>
        {isPlatformAdminUser(user) && (
          <DropdownMenuItem onSelect={() => go('/internal')} className="cursor-pointer text-zinc-700 focus:bg-zinc-50 focus:text-zinc-900">
            <FlaskConical className="h-4 w-4" strokeWidth={1.5}/>
            Open internal admin
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator className="bg-zinc-100"/>
        <DropdownMenuItem onSelect={onLogout} className="cursor-pointer text-zinc-700 focus:bg-zinc-50 focus:text-zinc-900">
          <LogOut className="h-4 w-4" strokeWidth={1.5}/>
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function NotificationMenu() {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button"
          aria-label="Notifications"
          title="Notifications"
          className="grid h-9 w-9 shrink-0 place-items-center rounded-sm border border-zinc-200 bg-white text-zinc-500 transition-colors hover:border-zinc-400 hover:text-zinc-900">
          <Bell className="h-4 w-4" strokeWidth={1.5}/>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="bottom" align="end" className="w-56 rounded-sm border-zinc-200 bg-white p-3 shadow-sm">
        <p className="text-sm text-zinc-500">No notifications yet</p>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function fallbackBreadcrumb(pathname) {
  if (pathname === '/dashboard') return [{ label: 'Dashboard', to: '/dashboard' }];
  if (pathname.startsWith('/dashboard/verifications')) return [
    { label: 'Dashboard', to: '/dashboard' },
    { label: 'Individuals', to: '/dashboard/verifications' },
  ];
  if (pathname.startsWith('/verifications')) return [
    { label: 'Dashboard', to: '/dashboard' },
    { label: 'Individuals', to: '/dashboard/verifications' },
  ];
  if (pathname.startsWith('/dashboard/businesses')) return [
    { label: 'Dashboard', to: '/dashboard' },
    { label: 'Businesses', to: '/dashboard/businesses' },
  ];
  if (pathname.startsWith('/dashboard/signatures')) return [{ label: 'Documents', to: '/dashboard/signatures' }];
  if (pathname.startsWith('/usage')) return [
    { label: 'Insights', to: '/usage' },
    { label: 'Usage', to: '/usage' },
  ];
  if (pathname.startsWith('/billing')) return [
    { label: 'Insights', to: '/billing' },
    { label: 'Billing', to: '/billing' },
  ];
  if (pathname.startsWith('/settings')) return [
    { label: 'Settings', to: '/settings' },
    { label: 'Organization', to: '/settings' },
  ];
  if (pathname.startsWith('/dashboard/integration')) return [
    { label: 'Developers', to: '/dashboard/integration' },
    { label: 'Integration', to: '/dashboard/integration' },
  ];
  if (pathname.startsWith('/dashboard/docs')) return [
    { label: 'Developers', to: '/dashboard/docs' },
    { label: 'Docs', to: '/dashboard/docs' },
  ];
  return [{ label: 'Dashboard', to: '/dashboard' }];
}

function topbarCrumbs(breadcrumb, pathname) {
  if (!breadcrumb) return fallbackBreadcrumb(pathname);
  const crumbs = [];
  if (breadcrumb.showDashboard) crumbs.push({ label: 'Dashboard', to: '/dashboard' });
  crumbs.push({ label: breadcrumb.rootLabel, to: breadcrumb.rootTo });
  if (breadcrumb.current) crumbs.push({ label: breadcrumb.current });
  return crumbs;
}

function DesktopTopbarBreadcrumb({ breadcrumb, pathname }) {
  const crumbs = topbarCrumbs(breadcrumb, pathname);
  return (
    <div className="hidden min-w-0 items-center gap-2 md:flex">
      <Link to="/dashboard" className="flex shrink-0 items-center gap-2">
        <LogoMark size={24} className="shrink-0"/>
        <span className="font-display text-lg font-bold tracking-tight">idemGO</span>
      </Link>
      <span className="shrink-0 font-mono text-xs text-zinc-300">/</span>
      <ol className="flex min-w-0 items-center gap-2 text-sm">
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1;
          return (
            <React.Fragment key={`${crumb.label}-${index}`}>
              {index > 0 && <ChevronRight className="h-3.5 w-3.5 shrink-0 text-zinc-300" strokeWidth={1.8}/>}
              <li className="min-w-0">
                {crumb.to && !isLast ? (
                  <Link to={crumb.to} className="text-zinc-400 hover:text-zinc-600">{crumb.label}</Link>
                ) : (
                  <span className={`${isLast ? 'font-medium text-zinc-900' : 'text-zinc-500'} block max-w-[32rem] truncate`} title={crumb.label}>
                    {crumb.label}
                  </span>
                )}
              </li>
            </React.Fragment>
          );
        })}
      </ol>
    </div>
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [breadcrumb, setBreadcrumb] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const breadcrumbContext = useMemo(() => ({ breadcrumb, setBreadcrumb }), [breadcrumb]);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed ? 'true' : 'false');
    } catch {
      // Ignore storage failures; the layout still works without persistence.
    }
  }, [sidebarCollapsed]);

  // Lock body scroll while the drawer is open.
  useEffect(() => {
    if (drawerOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  return (
    <DashboardBreadcrumbProvider value={breadcrumbContext}>
    <div className={`min-h-screen ${APP_CANVAS_BG}`}>
      {/* Fixed header */}
      <header data-testid="dashboard-header"
        className="fixed inset-x-0 top-0 z-40 h-16 bg-white border-b border-zinc-200">
        <div className="h-full px-4 md:px-6 flex items-center justify-between gap-3">
          <Link to="/dashboard" className="flex items-center gap-2 min-w-0 md:hidden" data-testid="dashboard-brand">
            <LogoMark size={25} className="shrink-0"/>
            <span className="font-display text-lg font-bold tracking-tight">idemGO</span>
            {user?.sandbox_mode && (
              <span data-testid="sandbox-badge"
                className="ml-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-mono font-semibold px-1.5 py-0.5 border border-amber-300 bg-amber-50 text-amber-800 shrink-0">
                <FlaskConical className="w-3 h-3" strokeWidth={2}/> Sandbox
              </span>
            )}
          </Link>
          <DesktopTopbarBreadcrumb breadcrumb={breadcrumb} pathname={location.pathname}/>
          <div className="flex items-center gap-2 md:gap-3 min-w-0">
            <NotificationMenu/>
            <AccountMenu
              user={user}
              className="hidden max-w-[240px] md:inline-flex"
              onNavigate={(path) => nav(path)}
              onLogout={() => { logout(); nav('/login'); }}
            />
            {/* Hamburger - mobile only, on the RIGHT */}
            <button type="button" data-testid="mobile-menu-toggle"
              onClick={() => setDrawerOpen(true)}
              aria-label="Open navigation"
              className="md:hidden w-9 h-9 grid place-items-center border border-zinc-200 rounded-sm hover:border-zinc-400 shrink-0">
              <Menu className="w-4 h-4" strokeWidth={1.5}/>
            </button>
          </div>
        </div>
      </header>

      {/* Fixed desktop sidebar */}
      <aside data-testid="dashboard-sidebar"
        className={`hidden md:flex fixed top-16 bottom-0 left-0 z-30 flex-col border-r border-zinc-100 bg-white py-6 transition-[width,padding] duration-200 ease-out ${sidebarCollapsed ? 'w-20 px-3' : 'w-52 px-4'}`}>
        <nav className="flex flex-1 flex-col gap-1 w-full overflow-y-auto" data-testid="dashboard-nav">
          <NavLinks collapsed={sidebarCollapsed}/>
        </nav>
        <div className={`mt-4 border-t border-zinc-100 pt-4 flex ${sidebarCollapsed ? 'justify-center' : 'justify-end'}`}>
          <button type="button"
            onClick={() => setSidebarCollapsed((current) => !current)}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-pressed={sidebarCollapsed}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="grid h-8 w-8 place-items-center rounded-sm border border-zinc-200 bg-white text-zinc-500 transition-colors hover:border-zinc-400 hover:text-zinc-900">
            {sidebarCollapsed
              ? <ChevronRight className="h-4 w-4" strokeWidth={1.6}/>
              : <ChevronLeft className="h-4 w-4" strokeWidth={1.6}/>}
          </button>
        </div>
      </aside>

      {/* Mobile slide-in drawer */}
      {drawerOpen && (
        <div className="md:hidden fixed inset-0 z-50" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDrawerOpen(false)}/>
          <aside className="absolute inset-y-0 right-0 w-64 bg-white border-l border-zinc-200 flex flex-col">
            <div className="h-16 px-4 border-b border-zinc-200 flex items-center justify-between">
              <span className="font-display text-lg font-bold tracking-tight">idemGO</span>
              <button type="button" data-testid="mobile-menu-close" onClick={() => setDrawerOpen(false)}
                aria-label="Close navigation"
                className="w-9 h-9 grid place-items-center border border-zinc-200 rounded-sm hover:border-zinc-400">
                <X className="w-4 h-4" strokeWidth={1.5}/>
              </button>
            </div>
            <nav className="p-4 flex-1 flex flex-col gap-1 overflow-y-auto" data-testid="mobile-nav">
              <NavLinks onNavigate={() => setDrawerOpen(false)}/>
            </nav>
            {/* Footer: user info + logout, mobile-only */}
            <div className="border-t border-zinc-200 p-4 space-y-3" data-testid="mobile-drawer-footer">
              <div className="text-xs font-mono text-zinc-500 truncate">{user?.email}</div>
              <button onClick={() => { setDrawerOpen(false); logout(); nav('/login'); }}
                data-testid="mobile-logout-btn"
                className="w-full inline-flex items-center justify-center gap-1.5 text-sm text-zinc-700 hover:text-black border border-zinc-200 px-3 py-2 rounded-sm hover:border-zinc-400 transition-colors">
                <LogOut className="w-3.5 h-3.5" strokeWidth={1.5}/> Logout
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Main content - offset by the fixed header (top) and sidebar (left on md+) */}
      <main className={`min-h-screen ${APP_CANVAS_BG} pt-16 transition-[padding] duration-200 ease-out ${sidebarCollapsed ? 'md:pl-20' : 'md:pl-52'}`}>
        <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-8 min-w-0">
          {user?.sandbox_mode && (
            <div data-testid="sandbox-banner"
              className="mb-6 flex items-start gap-3 border border-amber-400 bg-amber-50 px-4 py-3 rounded-sm">
              <FlaskConical className="w-4 h-4 mt-0.5 text-amber-700" strokeWidth={1.5}/>
              <div className="text-sm text-amber-900">
                <strong className="font-semibold">Sandbox mode is on.</strong> Verifications use deterministic fixtures based on the uploaded ID filename (<code className="font-mono text-xs">approved.jpg</code>, <code className="font-mono text-xs">rejected.jpg</code>, <code className="font-mono text-xs">review.jpg</code>). No real KYC verification performed. Disable in <Link to="/settings" className="underline">Settings</Link>.
              </div>
            </div>
          )}
          <Outlet />
        </div>
      </main>
    </div>
    </DashboardBreadcrumbProvider>
  );
}
